import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from '@mui/joy';
import api from '../../../lib/api';
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from '../../../components/common/ResponsiveTable';
import type {
  ReviewPage,
  WebsiteWorkbench,
} from '../../../types/websiteWorkspace';
import CheckpointPanel from './CheckpointPanel';
import WebsiteSeoWorkspace from './WebsiteSeoWorkspace';
import { apiMessage, filterWebsitePages, unpublished } from './workspaceLogic';
import { draftPreviewDocument } from './draftPreview';

export default function WebsiteWorkspace() {
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [state, setState] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [device, setDevice] = useState('100%');
  const root = `/marketing/website-builder/${projectId}`;
  const project = useQuery({
    queryKey: ['website-builder-project', projectId],
    queryFn: async () => (await api.websiteBuilder.getProject(projectId)).data,
  });
  const work = useQuery({
    queryKey: ['website-workbench', projectId],
    queryFn: async () =>
      (
        await api.get<WebsiteWorkbench>(
          `/api/website-builder/projects/${projectId}/workbench/`,
        )
      ).data,
  });
  const detail = useQuery({
    queryKey: ['website-review-document', projectId, selected],
    queryFn: async () =>
      (
        await api.get<{
          html: string;
          css: string;
          shared_css: string;
          digest: string;
          updated_at: string;
        }>(
          `/api/website-builder/projects/${projectId}/pages/${selected}/review-document/`,
        )
      ).data,
    enabled: selected !== null,
  });
  const selectedPage = work.data?.pages.find((page) => page.id === selected);
  const review = useMutation({
    mutationFn: (kind: 'comment' | 'approved') =>
      api.post(
        `/api/website-builder/projects/${projectId}/pages/${selected}/review/`,
        { kind, comment, base_digest: detail.data?.digest },
      ),
    onSuccess: () => {
      setComment('');
      cache.invalidateQueries({ queryKey: ['website-workbench', projectId] });
    },
  });
  if (project.isLoading || work.isLoading)
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress aria-label="Loading website workspace" />
      </Box>
    );
  if (project.isError || work.isError || !project.data || !work.data)
    return (
      <Alert color="danger">
        Could not load the website workspace.
        <Button
          onClick={() => {
            project.refetch();
            work.refetch();
          }}
        >
          Retry
        </Button>
      </Alert>
    );
  const site = project.data;
  const pages = work.data.pages;
  const pending = pages.filter(
    (page) => !['generated', 'approved'].includes(page.status),
  );
  const legal = pages.filter((page) => !page.legal_approval_valid);
  const changed = pages.filter(unpublished);
  const ready = pages.filter((page) => page.ready);
  const rows = filterWebsitePages(pages, search, type, state);
  const tab = ['launch', 'review', 'history', 'seo'].includes(
    params.get('tab') || '',
  )
    ? params.get('tab')!
    : 'launch';
  const columns: ResponsiveColumn<ReviewPage>[] = [
    {
      key: 'page',
      header: 'Page',
      mobileVariant: 'header',
      render: (page) => (
        <Stack>
          <Typography fontWeight="lg">{page.title}</Typography>
          <Typography level="body-xs">/{page.slug}</Typography>
          <Chip size="sm" variant="outlined">
            {page.page_type.replaceAll('_', ' ')}
          </Chip>
        </Stack>
      ),
    },
    {
      key: 'state',
      header: 'Draft / live',
      render: (page) => (
        <Stack spacing={0.5}>
          <Chip
            size="sm"
            color={page.status === 'failed' ? 'danger' : 'neutral'}
          >
            {page.status}
          </Chip>
          <Typography level="body-sm">
            {unpublished(page) ? 'Unpublished changes' : 'Published'}
          </Typography>
          <Typography level="body-xs">
            {page.published_at
              ? `Last published ${new Date(page.published_at).toLocaleString()}`
              : 'Not published yet'}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'review',
      header: 'Review',
      render: (page) => (
        <Chip
          size="sm"
          color={
            !page.legal_approval_valid
              ? 'warning'
              : page.ready
                ? 'success'
                : 'neutral'
          }
        >
          {!page.legal_approval_valid
            ? 'Legal approval needed'
            : page.ready
              ? 'Ready to publish'
              : 'Needs review'}
        </Chip>
      ),
    },
  ];
  return (
    <Stack spacing={3} sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography level="body-sm">Website workspace</Typography>
          <Typography level="h2">{site.name}</Typography>
          <Typography level="body-sm">
            Review saved drafts, prepare your launch, and keep a recoverable
            history.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button variant="outlined" onClick={() => navigate(`${root}/manage`)}>
            Site manager
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`${root}/settings`)}
          >
            Settings & domain
          </Button>
        </Box>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, value) => {
          if (!comment || window.confirm('Discard your unsaved review note?')) {
            setComment('');
            setParams({ tab: String(value) });
          }
        }}
        sx={{ borderRadius: 'lg' }}
      >
        <TabList sx={{ flexWrap: 'wrap' }}>
          <Tab value="launch">Launch checklist</Tab>
          <Tab value="review">Pages & review</Tab>
          <Tab value="history">History & restore</Tab>
          <Tab value="seo">SEO work queue</Tab>
        </TabList>
        <TabPanel value="launch">
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: 2,
              }}
            >
              {[
                [
                  'Generated pages',
                  `${pages.length - pending.length} / ${pages.length}`,
                  pending.length
                    ? 'Finish or retry incomplete pages'
                    : 'All page content is generated',
                ],
                [
                  'Unpublished drafts',
                  `${changed.length}`,
                  'Saved changes waiting for publishing',
                ],
                [
                  'Reviewed pages',
                  `${ready.length} / ${pages.length}`,
                  'Readiness applies to the exact saved draft',
                ],
                [
                  'Legal approval',
                  legal.length ? `${legal.length} needed` : 'Complete',
                  'Legal consent remains a separate requirement',
                ],
              ].map(([label, value, hint]) => (
                <Sheet
                  key={label}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 'md' }}
                >
                  <Typography level="body-sm">{label}</Typography>
                  <Typography level="h2" sx={{ my: 1 }}>
                    {value}
                  </Typography>
                  <Typography level="body-xs">{hint}</Typography>
                </Sheet>
              ))}
            </Box>
            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
              <Stack spacing={1}>
                <Typography level="title-lg">
                  What will publishing change?
                </Typography>
                <Typography>
                  Full-site publishing updates all{' '}
                  {
                    pages.filter((page) =>
                      ['generated', 'approved'].includes(page.status),
                    ).length
                  }{' '}
                  generated pages, shared header/footer, templates and site-wide
                  settings. {changed.length} pages currently have unpublished
                  drafts.
                </Typography>
                <Typography level="body-sm">
                  Pages with draft changes:{' '}
                  {changed.map((page) => page.title).join(', ') ||
                    'none detected'}
                  .
                </Typography>
                <Typography level="body-sm">
                  The preview and live site are separate. Refresh the preview
                  after restoring or changing saved content, then review before
                  publishing.
                </Typography>
              </Stack>
            </Sheet>
            {pending.length > 0 && (
              <Alert color="warning">
                Incomplete pages: {pending.map((page) => page.title).join(', ')}
                .
                <Button size="sm" onClick={() => navigate(`${root}/manage`)}>
                  Finish pages
                </Button>
              </Alert>
            )}
            {legal.length > 0 && (
              <Alert color="warning">
                Legal approval needed:{' '}
                {legal.map((page) => page.title).join(', ')}.
                <Button
                  size="sm"
                  onClick={() => navigate(`${root}/manage?page=${legal[0].id}`)}
                >
                  Review legal content
                </Button>
              </Alert>
            )}
            <Alert color="neutral">
              SEO checks: open the SEO work queue to run a saved-content audit.
              Domain hosting:{' '}
              {site.domain_status?.replaceAll('_', ' ') || 'default address'}.
              Public reachability has a separate check in Settings.
            </Alert>
            {site.validation_report?.passed === false && (
              <Alert color="warning">
                The last publishing validation reported issues. Review them in
                the site manager before publishing.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={() => setParams({ tab: 'review' })}>
                Review pages
              </Button>
              <Button
                variant="outlined"
                onClick={() => setParams({ tab: 'seo' })}
              >
                Check SEO
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(`${root}/preview`)}
              >
                Open preview
              </Button>
              <Button
                color="success"
                disabled={
                  !!pending.length ||
                  !!legal.length ||
                  !['site_review', 'deployed'].includes(site.status)
                }
                onClick={() => navigate(`${root}/manage?publish=1`)}
              >
                Review publishing confirmation
              </Button>
            </Box>
          </Stack>
        </TabPanel>
        <TabPanel value="review">
          <Stack spacing={2}>
            <CollapsibleFilters><Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Input
                placeholder="Search page title or URL"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Select
                aria-label="Page type"
                value={type}
                onChange={(_, value) => setType(value || '')}
              >
                <Option value="">All page types</Option>
                {Array.from(new Set(pages.map((page) => page.page_type)))
                  .sort()
                  .map((value) => (
                    <Option key={value} value={value}>
                      {value.replaceAll('_', ' ')}
                    </Option>
                  ))}
              </Select>
              <Select
                aria-label="Page status"
                value={state}
                onChange={(_, value) => setState(value || '')}
              >
                <Option value="">All statuses</Option>
                <Option value="unpublished">Unpublished</Option>
                <Option value="failed">Failed</Option>
                <Option value="pending">Pending</Option>
                <Option value="generated">Generated</Option>
              </Select>
              <Typography level="body-sm" sx={{ alignSelf: 'center' }}>
                {rows.length} of {pages.length} pages
              </Typography>
            </Box></CollapsibleFilters>
            <ResponsiveTable
              rows={rows}
              columns={columns}
              getRowKey={(page) => page.id}
              rowActions={(page) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={() => {
                      if (
                        !comment ||
                        window.confirm(
                          'Discard your unsaved note and switch pages?',
                        )
                      ) {
                        setSelected(page.id);
                        setComment('');
                        review.reset();
                      }
                    }}
                  >
                    Review
                  </Button>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => navigate(`${root}/manage?page=${page.id}`)}
                  >
                    Edit
                  </Button>
                </Box>
              )}
              emptyState={
                <Typography>No pages match these filters.</Typography>
              }
            />
            {selectedPage && (
              <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Stack spacing={2}>
                  <Typography level="h3">
                    Review: {selectedPage.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      ['Desktop', '100%'],
                      ['Tablet', '768px'],
                      ['Mobile', '375px'],
                    ].map(([label, width]) => (
                      <Button
                        key={label}
                        size="sm"
                        variant={device === width ? 'solid' : 'outlined'}
                        onClick={() => setDevice(width)}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>
                  {detail.isLoading && <CircularProgress />}
                  {detail.isError && (
                    <Alert color="danger">
                      Could not load this page.{' '}
                      <Button onClick={() => detail.refetch()}>Retry</Button>
                    </Alert>
                  )}
                  {detail.data && (
                    <Box
                      component="iframe"
                      title={`Saved draft: ${selectedPage.title}`}
                      sandbox=""
                      referrerPolicy="no-referrer"
                      srcDoc={draftPreviewDocument(
                        detail.data.html,
                        detail.data.shared_css,
                        detail.data.css,
                      )}
                      sx={{
                        width: device,
                        maxWidth: '100%',
                        height: 480,
                        border: '1px solid',
                        borderColor: 'divider',
                        mx: 'auto',
                      }}
                    />
                  )}
                  <Typography level="body-xs">
                    Read-only saved-draft preview. Forms and scripts are
                    disabled here. Marking ready does not publish or replace
                    legal consent.
                  </Typography>
                  <Textarea
                    placeholder="Add a review note for this saved version"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    minRows={3}
                    slotProps={{ textarea: { maxLength: 4000 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      loading={review.isPending}
                      disabled={!comment.trim()}
                      onClick={() => review.mutate('comment')}
                    >
                      Add note
                    </Button>
                    <Button
                      color="success"
                      variant="outlined"
                      disabled={
                        review.isPending ||
                        !selectedPage.legal_approval_valid ||
                        detail.isLoading ||
                        detail.isError ||
                        detail.data?.digest !== selectedPage.digest
                      }
                      onClick={() => review.mutate('approved')}
                    >
                      Mark reviewed draft ready
                    </Button>
                  </Box>
                  {review.isError && (
                    <Alert color="danger">
                      {apiMessage(
                        review.error,
                        'Could not save the review. Refresh to review the latest draft.',
                      )}
                      <Button
                        onClick={() => {
                          work.refetch();
                          detail.refetch();
                        }}
                      >
                        Refresh draft
                      </Button>
                    </Alert>
                  )}
                  {selectedPage.events.map((event) => (
                    <Sheet
                      key={event.id}
                      variant="soft"
                      sx={{ p: 1.5, borderRadius: 'sm' }}
                    >
                      <Typography level="title-sm">
                        {event.created_by} ·{' '}
                        {new Date(event.created_at).toLocaleString()}
                      </Typography>
                      <Typography>
                        {event.kind === 'approved'
                          ? 'Marked ready to publish'
                          : event.comment}
                      </Typography>
                      {event.kind === 'approved' && event.comment && (
                        <Typography>{event.comment}</Typography>
                      )}
                      {event.stale && (
                        <Chip size="sm" color="warning">
                          Earlier draft — review again
                        </Chip>
                      )}
                    </Sheet>
                  ))}
                </Stack>
              </Sheet>
            )}
          </Stack>
        </TabPanel>
        <TabPanel value="history">
          <CheckpointPanel
            projectId={projectId}
            checkpoints={work.data.checkpoints}
          />
        </TabPanel>
        <TabPanel value="seo">
          <WebsiteSeoWorkspace
            projectId={projectId}
            siteUrl={site.production_url}
            digest={work.data.digest}
          />
        </TabPanel>
      </Tabs>
    </Stack>
  );
}
